import { GameState } from '../game/GameState.js';
import type { MoveValidator } from '../game/MoveValidator.js';
import type { RuleSet } from '../game/rules/types.js';
import { opponentOf, type Color } from '../game/types.js';

const MAN_VALUE = 100;
const KING_VALUE = 300;
const CENTER_BONUS = 6;
const BACK_ROW_BONUS = 8;
const ADVANCEMENT_BONUS = 3; // per row toward the opponent's king row
const MOBILITY_WEIGHT = 1.5; // points per legal move
const TEMPO_BONUS = 4; // tiny edge for the side to move

/**
 * Heuristic position evaluator for Russian Shashki. Scores are in
 * centipawn-equivalent units; positive means the `perspective` side is ahead.
 *
 * Composition (from a fixed perspective, then negated for the other side):
 *  - **Material** dominates: man = 100, king = 300. Russian kings are flying,
 *    so the king premium is larger than in English draughts.
 *  - **Center control** — small bonus for pieces in the central 4×4.
 *  - **Back-row defense** — men still on their own first rank deny the
 *    opponent's nearest promotion squares.
 *  - **Advancement** — each row a man has crossed toward promotion.
 *  - **Mobility** — count of legal moves for each side; mobility starvation
 *    is what eventually wins king-vs-man endings here.
 *  - **Tempo** — a few points for whoever is to move, biasing the eval to
 *    prefer positions where *we* get to act first.
 */
export class BoardEvaluator {
  constructor(
    private readonly ruleSet: RuleSet,
    private readonly validator: MoveValidator,
  ) {}

  /**
   * Returns the heuristic score in centipawns from `perspective`'s point of
   * view. Higher = better for `perspective`. Terminal positions (no legal
   * moves) must be handled by the caller before invoking this.
   */
  evaluate(state: GameState, perspective: Color): number {
    const score = this.scoreWhitePerspective(state);
    return perspective === 'white' ? score : -score;
  }

  private scoreWhitePerspective(state: GameState): number {
    let white = 0;
    let black = 0;
    const size = state.boardSize;
    const centerLo = Math.floor(size / 2) - 2;
    const centerHi = Math.floor(size / 2) + 1;

    for (const { square, piece } of state.pieces()) {
      const isCenter =
        square.row >= centerLo &&
        square.row <= centerHi &&
        square.col >= centerLo &&
        square.col <= centerHi;
      let component = piece.kind === 'king' ? KING_VALUE : MAN_VALUE;
      if (isCenter) component += CENTER_BONUS;

      if (piece.kind === 'man') {
        const advancement =
          piece.color === 'white' ? size - 1 - square.row : square.row;
        component += advancement * ADVANCEMENT_BONUS;
        const backRow = piece.color === 'white' ? size - 1 : 0;
        if (square.row === backRow) component += BACK_ROW_BONUS;
      }

      if (piece.color === 'white') white += component;
      else black += component;
    }

    // Mobility — count legal moves for each side. Cheap to compute via the
    // validator but we have to swap the side-to-move to count the other side.
    const moverMoves = this.validator.legalMoves(state).length;
    const otherMoves = countMovesForOpponent(state, this.validator);

    const whiteMoves = state.currentPlayer === 'white' ? moverMoves : otherMoves;
    const blackMoves = state.currentPlayer === 'black' ? moverMoves : otherMoves;

    white += whiteMoves * MOBILITY_WEIGHT;
    black += blackMoves * MOBILITY_WEIGHT;

    let total = white - black;
    total += (state.currentPlayer === 'white' ? 1 : -1) * TEMPO_BONUS;
    return total;
  }

  /** Exposed for diagnostics / `ai/GameAnalysis` later. */
  get parameters() {
    return {
      manValue: MAN_VALUE,
      kingValue: KING_VALUE,
      centerBonus: CENTER_BONUS,
      backRowBonus: BACK_ROW_BONUS,
      advancementBonus: ADVANCEMENT_BONUS,
      mobilityWeight: MOBILITY_WEIGHT,
      tempoBonus: TEMPO_BONUS,
      ruleSetId: this.ruleSet.id,
    };
  }
}

function countMovesForOpponent(state: GameState, validator: MoveValidator): number {
  const flipped = GameState.custom(
    state.ruleSet,
    Array.from(state.pieces()),
    opponentOf(state.currentPlayer),
    {
      moveCount: state.moveCount,
      quietMoveCount: state.quietMoveCount,
      positionHistory: state.positionHistory,
    },
  );
  return validator.legalMoves(flipped).length;
}
